import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, FormGroup, Input, Label, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
  checkCrmData,
  createInstallation,
  getInstallationByIdentifier,
  updateInstallation,
} from "../../services/installationService";
import { AsyncButton, TooltipIcon } from "../common/buttons";
import InstallationDetailHeader from "./InstallationDetailHeader";
import { COMMUNICATION_LANGUAGES } from "./installationConstants";
import { FieldRow, Section, useInstallationRows } from "./installationRows";
import StorePickerModal from "./StorePickerModal";

const extractErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === "string") {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  const messages = Object.values(data)
    .flat()
    .filter((message) => typeof message === "string");
  return messages.length ? messages.join(" ") : fallback;
};

const SEND_RTI_COMMUNICATION_TOOLTIP =
  "When checked, the client receives RTI emails and WhatsApp messages. " +
  "Internal Slack alerts and the rest of the installation setup are unaffected.";

export default function InstallationDetail({ identifier }) {
  const history = useHistory();
  const { search } = useLocation();
  const queryStoreName = new URLSearchParams(search).get("store_name");
  const isNew = identifier === "new";

  const [installation, setInstallation] = useState(null);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [createMode, setCreateMode] = useState(isNew);
  const [editMode, setEditMode] = useState(isNew);
  const [editedData, setEditedData] = useState(() => {
    if (!isNew) {
      return {};
    }
    const prefill = { send_rti_communication: true };
    if (queryStoreName) {
      prefill.store_name = queryStoreName;
    }
    return prefill;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isStorePickerOpen, setIsStorePickerOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const { textRow, selectRow, numberRow, booleanRow, dateRow, stepRow } = useInstallationRows(
    installation ?? {},
    editMode && !createMode,
    editedData,
    setEditedData
  );

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    async function fetchInstallation() {
      try {
        const { data } = await getInstallationByIdentifier(identifier);
        if (!cancelled) setInstallation(data);
      } catch {
        if (!cancelled) setInstallation(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchInstallation();
    return () => {
      cancelled = true;
    };
  }, [identifier]);

  const handleToggleEdit = () => {
    setEditMode((prev) => !prev);
    setEditedData({});
  };

  const handleStartCreate = () => {
    setCreateMode(true);
    setEditMode(true);
    const prefill = { send_rti_communication: true };
    if (!isNew) {
      prefill.location = identifier;
    }
    if (queryStoreName) {
      prefill.store_name = queryStoreName;
    }
    setEditedData(prefill);
  };

  const handleCancelCreate = () => {
    setCreateMode(false);
    setEditMode(false);
    setEditedData({});
    setFieldErrors({});
  };

  const handleLinkExistingStore = () => {
    const confirmed = window.confirm(
      "Linking to an existing store bypasses the CRM data. Only do this if you know the store already exists. Continue?"
    );
    if (!confirmed) {
      return;
    }
    setIsStorePickerOpen(true);
  };

  const handleCheckCrmData = async () => {
    try {
      const { data } = await checkCrmData({ deal_id: editedData.deal_id });
      setEditedData((prev) => ({
        ...prev,
        location: data.location ?? "",
        store_name: data.store_name ?? "",
      }));
      setFieldErrors((prev) => ({ ...prev, location: null, store_name: null }));
      if (!data.location || !data.store_name) {
        toast.warning("CRM returned no location or store name — link an existing store instead.");
      }
    } catch {
      toast.error("Failed to fetch CRM data");
    }
  };

  const handleFieldChange = (key, value) => {
    setEditedData((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: null }));
  };

  const handleStoreSelect = ({ location, name }) => {
    setEditedData((prev) => ({ ...prev, location, store_name: name }));
    setFieldErrors((prev) => ({ ...prev, location: null, store_name: null }));
    setIsStorePickerOpen(false);
  };

  const validateRequiredFields = () => {
    const requiredFields = ["deal_id", "location", "store_name"];
    const errors = {};
    requiredFields.forEach((key) => {
      if (!editedData[key]) {
        errors[key] = "Required";
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (createMode) {
      const isValid = validateRequiredFields();
      if (!isValid) {
        return;
      }
    }
    setIsSaving(true);
    try {
      if (createMode) {
        const { data } = await createInstallation(editedData);
        toast.success("Installation created");
        history.push(`/installations/${data.uid}`);
      } else {
        const { data } = await updateInstallation(installation.uid, editedData);
        setInstallation(data);
        setEditedData({});
        setEditMode(false);
        toast.success("Installation updated");
      }
    } catch (error) {
      const fallback = createMode ? "Failed to create installation" : "Failed to update installation";
      toast.error(extractErrorMessage(error, fallback));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner color="primary" />
        </CardBody>
      </Card>
    );
  }

  if (!installation && !createMode) {
    return (
      <Card>
        <CardBody className="d-flex align-items-center gap-3">
          <p className="text-muted mb-0">No installation found.</p>
          <Button size="sm" color="primary" onClick={handleStartCreate}>
            Create installation
          </Button>
        </CardBody>
      </Card>
    );
  }

  const hasEdits = Object.keys(editedData).length > 0;
  const canCreate = Boolean(editedData.deal_id && editedData.location && editedData.store_name);

  function createField({ label, placeholder, value, onChange, error, children }) {
    return (
      <div className="d-flex flex-column gap-1">
        <div className="d-flex align-items-center gap-2">
          <small style={{ whiteSpace: "nowrap" }}>
            {label} <span className="text-danger">*</span>
          </small>
          <Input
            bsSize="sm"
            placeholder={placeholder}
            invalid={!!error}
            value={value}
            onChange={onChange}
            style={{ minWidth: 0 }}
          />
          {children}
        </div>
        {error && <small className="text-danger">{error}</small>}
      </div>
    );
  }

  function displayField(label, value) {
    return (
      <div className="d-flex align-items-center gap-2">
        <small style={{ whiteSpace: "nowrap" }}>{label}</small>
        <div className="form-control form-control-sm" style={{ minWidth: 0 }}>
          {value || <span className="text-muted">Loaded from CRM</span>}
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="d-flex flex-column gap-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <CardTitle tag="h4" className="mb-0">
              {createMode ? "New installation" : "Installation"}
            </CardTitle>
            {installation?.back_to_sales_reason && (
              <Badge color="warning" title={installation.back_to_sales_reason.description}>
                Back to sales
              </Badge>
            )}
            {installation?.send_rti_communication === false && (
              <Badge
                color="secondary"
                title="No communications sent to the client: they are not RTI-eligible, or the installation was created manually with the communication option turned off."
              >
                No communications
              </Badge>
            )}
          </div>
          <div className="d-flex gap-2">
            {(createMode || (editMode && hasEdits)) && (
              <Button size="sm" color="success" onClick={handleSave} disabled={isSaving || (createMode && !canCreate)}>
                {isSaving ? <Spinner size="sm" /> : "Save"}
              </Button>
            )}
            <Button
              size="sm"
              color={editMode ? "secondary" : "primary"}
              outline
              onClick={createMode ? handleCancelCreate : handleToggleEdit}
            >
              {editMode ? "Cancel" : "Edit"}
            </Button>
          </div>
        </div>
        {createMode ? (
          <div className="d-flex flex-column gap-2" style={{ maxWidth: "28rem" }}>
            {createField({
              label: "Deal ID",
              placeholder: "e.g. 504539046116",
              value: editedData.deal_id ?? "",
              error: fieldErrors.deal_id,
              onChange: (e) => handleFieldChange("deal_id", e.target.value),
              children: (
                <AsyncButton
                  size="sm"
                  color="primary"
                  outline
                  className="text-nowrap flex-shrink-0"
                  title="Fetches the store name and location from the CRM"
                  disabled={!editedData.deal_id}
                  onClick={handleCheckCrmData}
                >
                  Check CRM data
                </AsyncButton>
              ),
            })}
            {displayField("Location", editedData.location)}
            {displayField("Store name", editedData.store_name)}
            {(fieldErrors.location || fieldErrors.store_name) && (
              <small className="text-danger">Fetch the store from the CRM, or link an existing one below.</small>
            )}
            <FormGroup check className="mb-0">
              <Input
                type="checkbox"
                id="send-rti-communication"
                checked={editedData.send_rti_communication !== false}
                onChange={(e) => handleFieldChange("send_rti_communication", e.target.checked)}
              />
              <Label check htmlFor="send-rti-communication" className="d-inline-flex align-items-center gap-1">
                <small>Send RTI communication</small>
                <TooltipIcon tooltip={SEND_RTI_COMMUNICATION_TOOLTIP} />
              </Label>
            </FormGroup>
            <Button
              color="link"
              size="sm"
              className="text-muted p-0 align-self-start"
              style={{ fontSize: "0.75rem" }}
              onClick={handleLinkExistingStore}
            >
              Link to an existing store instead
            </Button>
          </div>
        ) : (
          <InstallationDetailHeader installation={installation} onInstallationUpdate={setInstallation} />
        )}
      </CardHeader>
      <CardBody>
        {installation?.back_to_sales_reason && (
          <Section title="Back to sales" variant="warning">
            <FieldRow
              label="Reason"
              viewContent={
                <span className="d-inline-flex align-items-center gap-1">
                  {installation.back_to_sales_reason.label}
                  <TooltipIcon tooltip={installation.back_to_sales_reason.description} />
                </span>
              }
            />
            {textRow("Comment", "back_to_sales_comment")}
          </Section>
        )}

        {installation?.debooked_at && (
          <Section title="Debooked">
            {dateRow("Debooked at", "debooked_at")}
            {textRow("Debooked by", "debooked_by")}
            {textRow("Reason", "debooking_reason")}
            {textRow("Note", "debooking_note")}
          </Section>
        )}

        <Section title="Current status">
          {stepRow("Step")}
          {dateRow("Created at", "created_at")}
          {dateRow("Updated at", "updated_at")}
          {dateRow("Submitted at", "submitted_at")}
          {textRow("Updated by", "updated_by")}
          {textRow("Case status", "case_status")}
          {textRow("Case owner", "case_owner_id")}
          {textRow("Account ID", "account_id")}
        </Section>

        <Section title="Store">
          {textRow("Location", "location")}
          {textRow("Store name", "store_name")}
          {textRow("Start date", "start_install_date", { editable: true })}
        </Section>

        <Section title="Client">
          {booleanRow("Send RTI communication", "send_rti_communication", {
            editable: true,
            nullable: false,
            tooltip: SEND_RTI_COMMUNICATION_TOOLTIP,
          })}
          {textRow("Client email", "client_email", { copy: true })}
          {textRow("Client name", "client_name")}
          {textRow("Country", "country_code")}
          {textRow("Language", "language_code")}
          {selectRow("Communication language", "communication_language", { options: COMMUNICATION_LANGUAGES })}
          {textRow("Account executive", "account_executive_email")}
        </Section>

        <Section title="Meeting">
          {textRow("Meeting link", "meeting_link")}
          {dateRow("Start time", "meeting_start_time")}
          {dateRow("Booked at", "meeting_booked_at")}
          {textRow("Contact reason", "meeting_contact_reason")}
        </Section>

        <Section title="Store infrastructure">
          {numberRow("Camera count", "camera_count", { editable: true })}
          {booleanRow("Is cloud", "is_cloud", { editable: true })}
          {numberRow("Upload speed (Mbps)", "upload_speed", { editable: true })}
        </Section>

        {(installation?.dvrs ?? []).length === 0 ? (
          <Section title="DVRs">
            <FieldRow label="DVRs" viewContent={<span className="text-muted">No DVRs</span>} />
          </Section>
        ) : (
          (installation?.dvrs ?? []).map((dvr, index) => (
            <Section key={dvr.id ?? index} title={`DVR ${index + 1}`}>
              <FieldRow label="Status" viewContent={dvr.status || null} />
              <FieldRow label="Brand" viewContent={dvr.dvr_brand || null} />
              <FieldRow label="Login" viewContent={dvr.dvr_login || null} />
              <FieldRow label="Password" viewContent={dvr.dvr_password || null} />
              <FieldRow label="IP" viewContent={dvr.dvr_ipv4 || null} />
              <FieldRow label="RTSP port" viewContent={dvr.dvr_rtsp_port != null ? String(dvr.dvr_rtsp_port) : null} />
              <FieldRow
                label="External RTSP port"
                viewContent={dvr.dvr_external_rtsp_port != null ? String(dvr.dvr_external_rtsp_port) : null}
              />
              <FieldRow label="HTTP port" viewContent={dvr.dvr_http_port != null ? String(dvr.dvr_http_port) : null} />
              <FieldRow
                label="External HTTP port"
                viewContent={dvr.dvr_external_http_port != null ? String(dvr.dvr_external_http_port) : null}
              />
              <FieldRow label="RTSP URL suffix" viewContent={dvr.rtsp_url_suffix || null} />
            </Section>
          ))
        )}

        <Section title="Router / Network">
          {textRow("Router brand", "router_brand", { editable: true })}
          {textRow("Router login", "router_login", { editable: true })}
          {textRow("Router password", "router_password", { editable: true })}
          {textRow("Gateway IP", "gateway_ipv4")}
          {textRow("WAN IP", "wan_ipv4")}
          {textRow("ISP", "isp_name", { editable: true })}
          {textRow("Network adapter", "network_adapter_type")}
          {booleanRow("CGNAT", "pitlane_is_cgnat")}
          {textRow("DDNS URL", "ddns_url", { editable: true })}
          {textRow("DDNS username", "ddns_username")}
          {textRow("DDNS password", "ddns_password")}
        </Section>

        <Section title="Remote access">
          {textRow("Remote access code", "remote_access_code", { editable: true })}
          {textRow("Remote access password", "remote_access_password")}
        </Section>

        <Section title="Local server">
          {textRow("Server name", "server_name")}
          {textRow("Tracking link", "tracking_link")}
        </Section>
      </CardBody>
      <StorePickerModal
        isOpen={isStorePickerOpen}
        onClose={() => setIsStorePickerOpen(false)}
        onSelect={handleStoreSelect}
      />
    </Card>
  );
}
